import { useAuthStore } from "@/stores/auth";
import { geniFetch } from "./geniBackend";
import { normalizeStoredImageRef } from "@/utils/imageRef";

/** Inline payloads that must be uploaded before persisting a good version. */
export function imageRefNeedsCrmUpload(value: string): boolean {
  const t = value.trim();
  if (!t) return false;
  if (t.startsWith("blob:") || t.startsWith("data:")) return true;
  if (t.startsWith("http://") || t.startsWith("https://")) return false;
  if (t.length < 80) return false;
  if (/^[A-Za-z0-9+/=\r\n]+$/.test(t)) return true;
  return false;
}

function coerceStringToDataUrlIfRawBase64(file: string): string {
  const t = file.trim();
  if (!t || t.startsWith("blob:") || t.startsWith("data:") || t.startsWith("http://") || t.startsWith("https://")) {
    return t;
  }
  if (t.length < 80 || !/^[A-Za-z0-9+/=\r\n]+$/.test(t)) {
    return t;
  }
  if (t.startsWith("/9j/")) return `data:image/jpeg;base64,${t}`;
  if (t.startsWith("iVBORw0KGgo")) return `data:image/png;base64,${t}`;
  if (t.startsWith("UklGR")) return `data:image/webp;base64,${t}`;
  if (t.length > 200) return `data:image/png;base64,${t}`;
  return t;
}

/**
 * Re-host a remote image URL via the backend (server-side fetch). Used for
 * marketplace CDN images (e.g. Kaspi `resources.cdn-kaspi.kz`) that block
 * cross-origin fetch from the browser. Returns a normalized stored ref.
 */
export const uploadImageFromUrl = async (url: string): Promise<string> => {
  const response = await geniFetch<{ url: string; fileName?: string }>("/uploads/images/from-url", {
    method: "POST",
    body: JSON.stringify({ url }),
  });
  return normalizeStoredImageRef(response.fileName || response.url);
};

export const uploadImageToCrm = async (file: Blob | string) => {
  // console.log(file)
  if (typeof file === "string") {
    file = coerceStringToDataUrlIfRawBase64(file);
    const isBlobOrDataUrl = file.startsWith("blob:") || file.startsWith("data:");
    const normalizedRef = normalizeStoredImageRef(file);
    if (normalizedRef !== file) {
      return normalizedRef;
    }

    if (!file.startsWith("http") && !isBlobOrDataUrl) {
      return file;
    }

    if (file.includes("https://my.zoomia.org/crm/files/")) {
      return file;
    }

    // Remote http(s) image (e.g. Kaspi CDN): re-host server-side. The browser
    // cannot fetch/canvas cross-origin non-CORS images (tainted canvas), so the
    // backend fetches and stores it. blob:/data: URLs stay on the canvas path below.
    if (file.startsWith("http")) {
      return uploadImageFromUrl(file);
    }

    file = await getBlobByUrl(file);
  }

  const authStore = useAuthStore();
  const _profileId = authStore.cardGeniusAccountProfile?.profileId;
  const jpegBlob = await toJpegBlobBrowser(file);
  const form = new FormData();
  form.append("file", jpegBlob, getRandomFileName() + ".jpg");
  const response = await geniFetch<{ url: string; fileName?: string }>("/uploads/images", {
    method: "POST",
    body: form,
  });
  return normalizeStoredImageRef(response.fileName || response.url);

  async function toJpegBlobBrowser(blobOrBase64: Blob | string) {
    const img = new Image();
    img.src = typeof blobOrBase64 === "string" ? blobOrBase64 : URL.createObjectURL(blobOrBase64);

    await img.decode();

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Failed to convert canvas to blob"));
          return;
        }
        resolve(blob);
      }, "image/jpeg", 0.9);
    });
  }

  function getRandomFileName() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  async function getBlobByUrl(url: string) {
    const res = await fetch(url, { cache: "no-store", mode: "cors" });

    const blob = await res.blob();

    return blob;
  }
};
